import { useEffect, useId, useState } from 'react';
import {
  BUTTON_CLASS,
  neutralButtonStyle,
  primaryButtonStyle,
  StatusDot,
  token,
} from '@bp-files/ui';
import { UPLOAD_COPY } from '@/components/UploadPanel/uploadPanel.copy';
import { FOLDER_COPY } from '@/components/FolderView/folderView.copy';
import { UploadDropTarget, type DropMode } from '@/components/UploadPanel/UploadDropTarget';
import { formatBytes } from '@/lib/formatBytes';
import { useUploadQueue, type QueueItem } from '@/lib/useUploadQueue';

/**
 * FIL-7: adding files, inside a folder.
 *
 * **Inline, above the table — not in a drawer.** Three reasons, in order of
 * weight. A drop target behind a click cannot receive a drag, because the
 * person is already holding files from their file manager and cannot open a
 * panel mid-drag. The queue has to stay visible for the length of a
 * multi-gigabyte transfer (UPL-3), and a drawer would overlay the very table it
 * is filling. And `CreateFolder` at the space root already sets the precedent:
 * a drawer is for a detour, and adding files to a folder is the folder's
 * primary act.
 *
 * **The SAS never reaches this component.** It lives inside the transfer
 * engine for the length of one request and is never stored, rendered, or logged
 * (SEC-3). What comes out here is a byte count.
 *
 * **`canWrite` gates what this renders, and that is visibility, not
 * authorization.** The API re-derives the right to write on every request and
 * refuses the observer side whatever this drew (SEC-5, CON-13). It is a
 * prop rather than the caller's mount condition, because **this component
 * mounts either way now**: the drop target it owns has to answer a drag on a
 * read-only folder too, and the stray-drop guard has to run there — it never
 * did before, so a missed drop on a read-only folder navigated the tab away
 * (ADR-0061). What `canWrite` still gates is the file input and the chooser.
 *
 * **The drop target is not a zone inside this component — it is the page.**
 * `UploadDropTarget` owns the window listeners and the overlay; this component
 * owns `accept()`, which both ways in call. There is exactly one entry point
 * for the OS picker and the drop, which is the whole reason the drop is handled
 * here rather than in `FolderView`.
 *
 * **`FolderView` puts its own trigger in the header, and this component's own
 * chooser steps aside for it rather than toggling.** See `showChooser` and
 * `inputId`'s own docs — the header's "Upload files" is a `<label
 * htmlFor>` pointed straight at the file input this component still owns, so
 * clicking it opens the OS picker in one step, with no intermediate panel to
 * open first. The queue and its warning are driven by `queue.active` /
 * `queue.items.length` regardless of `showChooser`, so a transfer already
 * running — or one that just finished — stays visible under the header
 * whether or not this component is drawing its own chooser.
 */

/**
 * The largest file the block scheme can address: 100 MiB × 50,000 ≈ 4.8 TiB.
 *
 * Mirrored by hand from `MAX_UPLOAD_BYTES` in `apps/api/src/shared/uploads.ts` —
 * separate workspaces with no shared package between them, exactly like the MSW
 * fixtures. Refusing here is a courtesy; the API refuses too (SEC-5).
 */
const MAX_UPLOAD_BYTES = 100 * 1024 * 1024 * 50_000;

export interface UploadPanelProps {
  spaceId: string;
  folderId: string;
  /** Called once per file that finalizes, so the table refetches (UPL-5). */
  onUploaded: () => void;
  /**
   * Whether the reader's agency may add to this space. Visibility only — the
   * API refuses the write regardless (SEC-5, CON-13). It gates the file input
   * and the chooser; it does **not** gate the drop target, which answers a drag
   * on a read-only folder rather than leaving the gesture unanswered.
   */
  canWrite: boolean;
  /**
   * What a drag over the page should do — forwarded to `UploadDropTarget`
   * unchanged and not interpreted here. `FolderView` computes it, because the
   * facts that suppress it (an open viewer, an open consent dialog, a space
   * that has not answered yet) are its own.
   */
  drop: DropMode;
  /** Named in the overlay, when the caller knows it. Forwarded, not read. */
  folderName: string | undefined;
  /**
   * Whether this component draws its own heading, subline, and chooser button.
   * Defaults to `true` — a caller with no header trigger of its own (this
   * file's own test) gets the fully self-contained picker. `FolderView` passes
   * `false`: its header carries the single "Upload files" trigger, and a
   * second, internal chooser here would be a redundant second way to start the
   * same act — the thing collapsing it into one button was for.
   *
   * **It no longer governs a drop zone**, because there is no zone: the page is
   * the target (ADR-0061). The dashed box this used to draw was reachable from
   * no screen in the product for four milestones, since `FolderView` has passed
   * `false` since the header collapsed.
   *
   * The file input itself is unaffected — it renders either way (given
   * `canWrite`), because `false` doesn't mean "no picker", it means "the trigger
   * lives elsewhere". Pair with `inputId` so that outside trigger can reach it.
   */
  showChooser?: boolean;
  /**
   * The id the file input renders with. Only worth passing when `showChooser`
   * is `false`: an external `<label htmlFor={inputId}>` — `FolderView`'s
   * header button — is how the OS picker opens with no chooser of this
   * component's own on screen to click first. A `<label>` and the input it
   * targets need no DOM proximity, only a matching id, so the trigger can sit
   * in a header row this component never renders into.
   *
   * Defaults to an id generated with `useId()`, for the `showChooser: true`
   * case, where this component's own internal label is what needs it.
   */
  inputId?: string;
}

export function UploadPanel({
  spaceId,
  folderId,
  onUploaded,
  canWrite,
  drop,
  folderName,
  showChooser = true,
  inputId: externalInputId,
}: UploadPanelProps) {
  const queue = useUploadQueue({ spaceId, folderId }, onUploaded);
  const [refusals, setRefusals] = useState<string[]>([]);
  const generatedInputId = useId();
  const inputId = externalInputId ?? generatedInputId;

  useUnloadGuard(queue.active);

  function accept(incoming: readonly File[], directoryNames: readonly string[] = []) {
    // First, and deliberately: a directory arrives as a zero-byte `File`, so
    // the empty-file branch below would otherwise claim it. There are no nested
    // folders (FIL-2), so this is a thing the reader can act on.
    const refused: string[] = directoryNames.map((name) => UPLOAD_COPY.refusals.folder(name));
    const accepted: File[] = [];

    for (const file of incoming) {
      // The API's `sizeBytes` must be positive and bounded, so both of these
      // would be a 400. A round trip is a poor way to learn that a file is
      // empty — and people drop empty files.
      if (file.size === 0) refused.push(UPLOAD_COPY.refusals.empty(file.name));
      else if (file.size > MAX_UPLOAD_BYTES) refused.push(UPLOAD_COPY.refusals.tooLarge(file.name));
      else accepted.push(file);
    }

    setRefusals(refused);
    if (accepted.length > 0) queue.add(accepted);
  }

  // Shared by both branches below — the input a `showChooser: false` caller's
  // external label targets is the same element this component's own internal
  // one would have pointed at, just reached from outside instead of from the
  // chooser beside it. Absent entirely without `canWrite`: there would be no
  // label anywhere pointing at it.
  const input = !canWrite ? null : (
    <input
      id={inputId}
      type="file"
      multiple
      className="sr-only"
      onChange={(event) => {
        accept([...(event.target.files ?? [])]);
        // Cleared so picking the same file twice fires `change` twice.
        event.target.value = '';
      }}
    />
  );

  return (
    <section className="flex flex-col gap-3">
      {/*
        The page, not a zone (FIL-7, ADR-0061). It mounts under every mode,
        including `off` — suppression means the overlay does not paint and the
        files are discarded, never that the window stops being listened to.
        A drop that reaches the browser navigates the tab to `file:///…` and
        kills every transfer in flight.
      */}
      <UploadDropTarget mode={drop} folderName={folderName} onFiles={accept} />

      {showChooser && canWrite ? (
        <>
          <div className="flex flex-col gap-1">
            {/*
              `<h2>`, matching `SpaceRoot`'s folder section and
              a per-space section. **Nothing renders this today** —
              `FolderView` passes `showChooser={false}` and opens the OS picker
              from its header label instead, so only this component's own test
              (which takes the default) ever sees it. The level is right for the
              caller it would have: a section inside a folder page whose title
              is the `<h1>`.
            */}
            <h2 className="text-sm font-semibold" style={{ color: token('bp-text') }}>
              {UPLOAD_COPY.heading}
            </h2>
            <p className="text-xs" style={{ color: token('bp-text-secondary') }}>
              {UPLOAD_COPY.subline}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label htmlFor={inputId} className={BUTTON_CLASS} style={primaryButtonStyle()}>
              {UPLOAD_COPY.choose}
            </label>

            {input}
          </div>
        </>
      ) : (
        // No chooser of its own to hold it — `FolderView`'s header label
        // targets this same id directly.
        input
      )}

      {/*
        The one durability statement, and it is a warning. Resume is bounded by
        the page, so nothing here may imply a transfer outlives the tab.

        **It gets louder while a transfer is in flight**, which is the moment it
        is actually load-bearing: the pulsing amber dot is the design system's
        own device for a live signal, and amber for "active" is one of its two
        sanctioned uses. It is a `role="status"` there rather than an `alert` —
        nothing has failed, something is simply happening — and the sentence
        itself never changes, because a warning that softens when it matters
        most is worse than no warning at all.

        **It ignores `showChooser` on the active branch, and follows a wider
        condition on the idle one.** A transfer in flight has to stay
        announced regardless of which component drew the chooser that started
        it. Idle, the reminder shows whenever this component's own dropzone is
        on screen (`showChooser`) **or** the queue still holds something —
        recently settled items are still worth "don't worry, that already
        finished" context — but not for a `showChooser: false` caller sitting
        fully idle, which has nothing on screen for the reminder to be about.
      */}
      {queue.active ? (
        <p
          role="status"
          aria-label={UPLOAD_COPY.stayOnPage}
          className="flex items-center gap-2 text-xs font-medium"
          style={{ color: token('bp-text') }}
        >
          <StatusDot tone="amber" size="sm" pulse />
          {UPLOAD_COPY.stayOnPage}
        </p>
      ) : showChooser || queue.items.length > 0 ? (
        <p className="text-xs" style={{ color: token('bp-text-secondary') }}>
          {UPLOAD_COPY.stayOnPage}
        </p>
      ) : null}

      {/*
        Unconditional on `showChooser`: a refusal can be produced by whichever
        chooser is live — this component's own, the header's external label, or
        a drop on the page — and whichever one it was, the reader who just
        offered an empty file, an oversized one, or a folder needs to be told
        regardless.
      */}
      {refusals.map((refusal) => (
        <p key={refusal} role="alert" className="flex items-center gap-2 text-xs text-danger">
          <StatusDot tone="alert" size="sm" />
          {refusal}
        </p>
      ))}

      {queue.items.length === 0 ? null : (
        <ul className="flex flex-col gap-2">
          {queue.items.map((item) => (
            <li key={item.id}>
              <TransferRow
                item={item}
                onRetry={() => {
                  queue.retry(item.id);
                }}
                onDismiss={() => {
                  queue.dismiss(item.id);
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * One transfer.
 *
 * A plain list rather than `DataTable`: that composition renders rows the
 * server owns, and these are client state with a control apiece.
 */
function TransferRow({
  item,
  onRetry,
  onDismiss,
}: {
  item: QueueItem;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  const settled = item.state.kind === 'done' || item.state.kind === 'failed';
  const sent = item.state.kind === 'uploading' ? item.state.bytesSent : 0;

  return (
    <div
      className="flex flex-col gap-2 border p-3"
      style={{
        background: token('bp-card'),
        borderColor: token('bp-border'),
        borderRadius: token('radius-md'),
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium" style={{ color: token('bp-text') }}>
          {item.name}
        </span>

        <span
          className="text-xs"
          style={{ color: token('bp-text-secondary'), fontVariantNumeric: 'tabular-nums' }}
        >
          {item.state.kind === 'uploading'
            ? UPLOAD_COPY.queue.of(
                formatBytes(sent, FOLDER_COPY.units),
                formatBytes(item.sizeBytes, FOLDER_COPY.units),
              )
            : formatBytes(item.sizeBytes, FOLDER_COPY.units)}
        </span>
      </div>

      {item.state.kind === 'uploading' ? (
        <ProgressBar
          label={UPLOAD_COPY.queue.progress(item.name)}
          fraction={item.sizeBytes === 0 ? 0 : sent / item.sizeBytes}
        />
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <StateLine item={item} />

        <div className="flex items-center gap-2">
          {item.state.kind === 'failed' && !item.state.permanent ? (
            <button
              type="button"
              onClick={onRetry}
              className={BUTTON_CLASS}
              style={neutralButtonStyle()}
            >
              {UPLOAD_COPY.queue.retry}
            </button>
          ) : null}

          {settled ? (
            <button
              type="button"
              aria-label={UPLOAD_COPY.queue.dismiss(item.name)}
              onClick={onDismiss}
              className={BUTTON_CLASS}
              style={neutralButtonStyle()}
            >
              {'×'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * What is happening, in the words of the step it is on.
 *
 * `committing` and `finalizing` are named rather than folded into "done"
 * because the bar is already full by then: a five-gigabyte file would otherwise
 * sit at 100% for the length of a block-list commit, which reads as a hang.
 */
function StateLine({ item }: { item: QueueItem }) {
  if (item.state.kind === 'failed') {
    return (
      <span
        role="alert"
        className="flex flex-wrap items-center gap-2 text-xs"
        style={{ color: token('bp-danger') }}
      >
        <StatusDot tone="alert" size="sm" />

        {/* The API's own sentence where it gave one — it says what to do next
            in a way no status code does. Its own element, so it reads as one
            statement rather than a sentence assembled from fragments. */}
        <span>{item.state.detail ?? UPLOAD_COPY.queue.failed}</span>

        {item.state.permanent ? <span>{UPLOAD_COPY.queue.permanent}</span> : null}
      </span>
    );
  }

  const label =
    item.state.kind === 'waiting'
      ? UPLOAD_COPY.queue.waiting
      : item.state.kind === 'done'
        ? UPLOAD_COPY.queue.done
        : UPLOAD_COPY.queue[item.state.phase];

  return (
    <span className="text-xs" style={{ color: token('bp-text-secondary') }}>
      {label}
    </span>
  );
}

/**
 * A bar, local to this component.
 *
 * Not a `packages/ui` composition: one consumer, and Article 3 declines a
 * shared abstraction until a second one exists. Promote it when the Storage
 * dashboard (STO-1) needs one, in its own PR.
 */
function ProgressBar({ label, fraction }: { label: string; fraction: number }) {
  const percent = Math.round(Math.min(Math.max(fraction, 0), 1) * 100);

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      className="h-1 w-full overflow-hidden"
      style={{ background: token('bp-panel'), borderRadius: token('radius-full') }}
    >
      <div
        className="h-full transition-all"
        style={{ width: `${String(percent)}%`, background: token('bp-blue') }}
      />
    </div>
  );
}

/**
 * The browser's own "leave site?" warning, while anything is in flight.
 *
 * Resume is bounded by the page — a `File` handle does not survive a reload —
 * so this is the only thing standing between somebody and a lost transfer.
 * Browsers ignore custom text, which is why there is no copy for it.
 */
function useUnloadGuard(active: boolean): void {
  useEffect(() => {
    if (!active) return;

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
    };
  }, [active]);
}

/*
 * `useStrayDropGuard` used to live here — window listeners that swallowed a
 * drop missing the target, so the browser could not navigate the tab to
 * `file:///…` and kill every transfer in flight. It is `UploadDropTarget`'s job
 * now, and there is exactly one set of window drag listeners in this app
 * (ADR-0061). Two would be the second mechanism Article 3 forbids, and the
 * absorbing version covers a case this one never did: a read-only folder, where
 * this component did not mount at all.
 */
